import { useEffect, useState } from 'react'
import { useUserData } from '../services/user/user-data'
import { MARKER_SUBREGEX } from './constants'
import { useInitMarkerProductData } from './marker-product-data'

const getMarkerRegex = (replacementPattern: string) =>
  new RegExp(`\\$${replacementPattern}_${MARKER_SUBREGEX}`, 'g')

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getValueByPath = (obj: any, path: string): any => path.split('.').reduce((acc, key) => acc?.[key], obj)

interface AdaptationPatternModel {
  markerData: { [textKey: string]: string }
  data: { [textKey: string]: string }
  pattern: string
  isEditMode: boolean
}

const getAdaptedPatternValue = ({
  markerData,
  data,
  pattern,
  isEditMode = false
}: AdaptationPatternModel) => {
  if (!markerData) {
    return undefined
  }

  const updatedData: { [key: string]: string } = {}
  const regex = getMarkerRegex(pattern)

  Object.entries(markerData).forEach(([key, value]) => {
    if (!value) {
      updatedData[key] = value
      return
    }

    const replaced = value.replace(regex, (_match, path) => {
      const replacement = getValueByPath(data, path)
      return replacement ?? (isEditMode ? `$${pattern}_${path}$` : '')
    })

    updatedData[key] = replaced
  })

  return updatedData
}

const getMappedMarkerData = (
  markerData: { [key: string]: string },
  isEditMode: boolean = false
) =>
  (isEditMode
    ? markerData
    : Object.keys(markerData)
        .map((key: string) => ({ [key]: '' }))
        .reduce(
          (accumulator, currentValue) =>
            Object.assign(accumulator, currentValue),
          {}
        ))

export const useDataMarkerReplace = (
  markerData: { [key: string]: string },
  pattern: string,
  isEditMode: boolean = false
): { [key: string]: string } => {
  const [values, setValues] = useState(
    getMappedMarkerData(markerData, isEditMode)
  )

  const userData = useUserData()
  const productData = useInitMarkerProductData()

  const match = pattern?.match(/\$(\w+?)_/)

  const dataMarkerPattern = match ? match[1] : null

  useEffect(() => {
    const adaptedValue = getAdaptedPatternValue({
      markerData,
      data: { ...productData, ...userData?.user?.profile },
      pattern: dataMarkerPattern || '',
      isEditMode
    })
    // stop useEffect from being called in a loop if state didn't change
    if (
      JSON.stringify(values) !== JSON.stringify(adaptedValue) &&
      adaptedValue
    ) {
      setValues(adaptedValue)
    }
  }, [userData, markerData])

  return { ...values }
}
